import React, { useState, useEffect } from 'react';
import { Users, UserCheck, UserX, UserPlus } from 'lucide-react';
import UserDirectoryTab from './components/UserDirectoryTab';
import AddImportUsersTab from './components/AddImportUsersTab';
import DeactivationLogTab from './components/DeactivationLogTab';
import useUsersStore from '@/stores/AdminStore/UsersStore';
import { LoadingSpinner, ErrorDisplay } from '@/Components/common';
import './Users.css';

const UserManagementPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('directory');

  const {
    users,
    stats,
    deactivationLogs,
    loadingUsers,
    loadingStats,
    error,
    fetchUsers,
    fetchStats,
    fetchDeactivationLogs,
    fetchDepartments,
  } = useUsersStore();

  // Initial data load
  useEffect(() => {
    fetchStats();
    fetchDepartments();
  }, [fetchStats, fetchDepartments]);

  // Lazy load data based on active tab
  useEffect(() => {
    if (activeTab === 'directory' && users.length === 0) {
      fetchUsers();
    }
  }, [activeTab, users.length, fetchUsers]);

  useEffect(() => {
    if (activeTab === 'deactivation' && deactivationLogs.length === 0) {
      fetchDeactivationLogs();
    }
  }, [activeTab, deactivationLogs.length, fetchDeactivationLogs]);

  if (loadingUsers && loadingStats && users.length === 0) {
    return (
      <div className="trainer-user-page-container">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error && users.length === 0) {
    return (
      <div className="trainer-user-page-container">
        <ErrorDisplay
          title="Failed to Load User Data"
          message={error}
          type="error"
          showRetry
          onRetry={() => {
            fetchUsers();
            fetchStats();
          }}
        />
      </div>
    );
  }

  return (
    <div className="trainer-user-page-container">
      <div className="trainer-user-page-breadcrumb">
        <span>User Management</span>
        <span>›</span>
        <span>User</span>
      </div>

      <div className="trainer-user-page-header">
        <h1 className="trainer-user-page-title">User Management</h1>
        <p className="trainer-user-page-subtitle">Manage users, teams, and learning assignments</p>
      </div>

      <div className="trainer-user-page-tabs">
        <button
          className={`trainer-user-page-tab ${activeTab === 'directory' ? 'active' : ''}`}
          onClick={() => setActiveTab('directory')}
        >
          User Directory
        </button>
        <button
          className={`trainer-user-page-tab ${activeTab === 'import' ? 'active' : ''}`}
          onClick={() => setActiveTab('import')}
        >
          Add/Import Users
        </button>
        <button
          className={`trainer-user-page-tab ${activeTab === 'deactivation' ? 'active' : ''}`}
          onClick={() => setActiveTab('deactivation')}
        >
          Deactivation Log
        </button>
      </div>

      {activeTab === 'directory' && (
        <>
          <div className="trainer-user-page-stats">
            <div className="trainer-user-page-stat-card">
              <div className="trainer-user-page-stat-info">
                <h3>Total Users</h3>
                <p>{loadingStats ? '-' : stats?.totalUsers || 0}</p>
              </div>
              <div className="trainer-user-page-stat-icon orange">
                <Users size={24} />
              </div>
            </div>
            <div className="trainer-user-page-stat-card">
              <div className="trainer-user-page-stat-info">
                <h3>Active</h3>
                <p>{loadingStats ? '-' : stats?.activeUsers || 0}</p>
              </div>
              <div className="trainer-user-page-stat-icon green">
                <UserCheck size={24} />
              </div>
            </div>
            <div className="trainer-user-page-stat-card">
              <div className="trainer-user-page-stat-info">
                <h3>Inactive</h3>
                <p>{loadingStats ? '-' : stats?.inactiveUsers || 0}</p>
              </div>
              <div className="trainer-user-page-stat-icon gray">
                <UserX size={24} />
              </div>
            </div>
            <div className="trainer-user-page-stat-card">
              <div className="trainer-user-page-stat-info">
                <h3>New This Month</h3>
                <p>{loadingStats ? '-' : stats?.newThisMonth || 0}</p>
              </div>
              <div className="trainer-user-page-stat-icon blue">
                <UserPlus size={24} />
              </div>
            </div>
          </div>

          <UserDirectoryTab users={users} />
        </>
      )}

      {activeTab === 'import' && <AddImportUsersTab />}

      {activeTab === 'deactivation' && <DeactivationLogTab deactivationLogs={deactivationLogs} />}
    </div>
  );
};

export default UserManagementPage;
